import { MenuItem, Select, Stack, useMediaQuery, type Theme } from '@mui/material';

export interface PillTabOption {
  value: string;
  label: string;
}

interface PillTabsProps {
  options: PillTabOption[];
  value: string;
  onChange: (value: string) => void;
}

/**
 * Sub-views as a segmented pill control (foundations.md §7.14 "Tabs as
 * pills"): no underline, no background band — active = teal-ink CTA fill
 * (hybrid-3c v3.8) + white + soft shadow, inactive = transparent + secondary
 * text. Collapses to a Select on mobile.
 */
export function PillTabs({ options, value, onChange }: PillTabsProps) {
  const isNarrow = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  if (isNarrow) {
    return (
      <Select
        size="small"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        sx={{ borderRadius: 999, minWidth: 160 }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} sx={{ bgcolor: 'transparent' }}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <Stack
            key={option.value}
            component="button"
            type="button"
            onClick={() => onChange(option.value)}
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{
              height: 30,
              px: 2,
              border: 0,
              borderRadius: 999,
              cursor: 'pointer',
              fontSize: '0.8125rem',
              fontWeight: 600,
              fontFamily: 'inherit',
              color: active ? 'common.white' : 'text.secondary',
              bgcolor: active ? (theme) => theme.custom.ctaFill : 'transparent',
              boxShadow: active ? (theme) => theme.custom.cardShadow : 'none',
              transition: 'background-color 160ms, color 160ms',
              '&:hover': {
                bgcolor: active ? (theme) => theme.custom.ctaFillHover : 'action.hover',
              },
            }}
          >
            {option.label}
          </Stack>
        );
      })}
    </Stack>
  );
}
